import React, { useState } from 'react';
import { X } from 'lucide-react';
import {
  DocumentRecord,
  DocumentVersionRecord,
  DocumentReview,
  DocumentApproval,
  DocumentAcknowledgement,
  DocumentCrossLink,
  DocumentStatusHistory,
} from '../types';
import { documentsService } from '../documentsService';

interface Props {
  document: DocumentRecord;
  isOpen: boolean;
  onClose: () => void;
  onRefresh: () => void;
}

export const DocumentDetailModal: React.FC<Props> = ({
  document,
  isOpen,
  onClose,
  onRefresh,
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'versions' | 'workflow' | 'acks' | 'history'>('details');
  const [versions, setVersions] = useState<DocumentVersionRecord[]>([]);
  const [history, setHistory] = useState<DocumentStatusHistory[]>([]);
  const [acks, setAcks] = useState<DocumentAcknowledgement[]>([]);
  const [crossLinks, setCrossLinks] = useState<DocumentCrossLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      loadDetails();
    }
  }, [isOpen, document.id]);

  const loadDetails = async () => {
    setLoading(true);
    try {
      const [vList, hList, aList, clList] = await Promise.all([
        documentsService.getVersions(document.id),
        documentsService.getStatusHistory(document.id),
        documentsService.getAcknowledgements({ document_id: document.id }),
        documentsService.getCrossLinks(document.id),
      ]);
      setVersions(vList);
      setHistory(hList);
      setAcks(aList);
      setCrossLinks(clList);
    } catch (e: any) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Actions
  const handleRequestReview = async () => {
    setActionError(null);
    setActionSuccess(null);
    try {
      const currentVerId = document.current_version_id || versions[0]?.id;
      if (!currentVerId) throw new Error('Version not found');

      await documentsService.requestReview(document.id, {
        version_id: currentVerId,
        reviewer: { id: 'USR-QA-01', name: 'Selin Ozturk', role: 'QUALITY_REVIEWER' },
        actor: { id: document.owner_id, name: document.owner_name },
      });
      setActionSuccess('Technical review request submitted successfully.');
      onRefresh();
      loadDetails();
    } catch (err: any) {
      setActionError(err.message || 'An error occurred');
    }
  };

  const handleSubmitForApproval = async () => {
    setActionError(null);
    setActionSuccess(null);
    try {
      const currentVerId = document.current_version_id || versions[0]?.id;
      if (!currentVerId) throw new Error('Version not found');

      await documentsService.submitForApproval(document.id, {
        version_id: currentVerId,
        approver: { id: 'USR-AM-01', name: 'Cemil Demir (Accountable Manager)', role_title: 'Accountable Manager' },
        actor: { id: document.owner_id, name: document.owner_name },
      });
      setActionSuccess('Document submitted for Accountable Manager approval.');
      onRefresh();
      loadDetails();
    } catch (err: any) {
      setActionError(err.message || 'An error occurred');
    }
  };

  const handlePublish = async () => {
    setActionError(null);
    setActionSuccess(null);
    try {
      const currentVerId = document.current_version_id || versions[0]?.id;
      if (!currentVerId) throw new Error('Version not found');

      await documentsService.publishDocument(document.id, {
        version_id: currentVerId,
        effectiveDate: new Date().toISOString(),
        actor: { id: 'USR-AM-01', name: 'Cemil Demir' },
        distribution_user_ids: ['USR-FO-01', 'USR-QA-01', 'USR-SEC-01'],
      });
      setActionSuccess('Document published successfully and acknowledgement notifications distributed.');
      onRefresh();
      loadDetails();
    } catch (err: any) {
      setActionError(err.message || 'An error occurred');
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-xl">
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#4338ca', fontSize: '16px' }}>
                {document.document_number}
              </span>
              <span style={{ fontSize: '11px', fontWeight: 600, background: '#e2e8f0', color: '#1e293b', padding: '2px 8px', borderRadius: '4px' }}>
                v{document.current_version_number || '1.0'}
              </span>
              <span style={{ fontSize: '11px', fontWeight: 700, background: '#e0e7ff', color: '#3730a3', padding: '2px 8px', borderRadius: '4px', border: '1px solid #c7d2fe' }}>
                {document.status}
              </span>
            </div>
            <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: 0, lineHeight: 1.35 }}>{document.title}</h2>
          </div>
          <button
            onClick={onClose}
            className="modal-close-btn"
            title="Kapat"
            aria-label="Kapat"
          >
            <X size={20} />
          </button>
        </div>

        {/* Action feedback */}
        {actionError && (
          <div className="mx-6 mt-4 p-3 text-xs bg-rose-50 border border-rose-200 text-rose-700 rounded-lg">
            {actionError}
          </div>
        )}
        {actionSuccess && (
          <div className="mx-6 mt-4 p-3 text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg">
            {actionSuccess}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-200 px-6 bg-white space-x-6">
          <button
            onClick={() => setActiveTab('details')}
            className={`py-3 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === 'details'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            General Info & Scope
          </button>
          <button
            onClick={() => setActiveTab('workflow')}
            className={`py-3 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === 'workflow'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            Review & Approval (SoD)
          </button>
          <button
            onClick={() => setActiveTab('versions')}
            className={`py-3 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === 'versions'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            Versions ({versions.length})
          </button>
          <button
            onClick={() => setActiveTab('acks')}
            className={`py-3 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === 'acks'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            Acknowledgements ({acks.length})
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-3 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === 'history'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            History / Audit Trail
          </button>
        </div>

        {/* Tab Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {activeTab === 'details' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200">
                <div>
                  <div className="text-xs text-slate-500">Department</div>
                  <div className="text-sm font-semibold text-slate-900">{document.department}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Category</div>
                  <div className="text-sm font-semibold text-slate-900">{document.category_name || document.category_id}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Confidentiality Level</div>
                  <div className="text-sm font-semibold text-slate-900">{document.confidentiality}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Review Cycle</div>
                  <div className="text-sm font-semibold text-slate-900">{document.review_cycle_months} Months</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Document Owner</div>
                  <div className="text-sm font-semibold text-slate-900">{document.owner_name}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Quality Reviewer</div>
                  <div className="text-sm font-semibold text-slate-900">{document.controller_name || 'Unassigned'}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Effective Date</div>
                  <div className="text-sm font-semibold text-slate-900">{document.effective_date ? document.effective_date.slice(0, 10) : 'Draft / Not Set'}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Next Review</div>
                  <div className="text-sm font-semibold text-slate-900">{document.next_review_date ? document.next_review_date.slice(0, 10) : '-'}</div>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Scope & Purpose</h4>
                <p className="text-sm text-slate-700 bg-white border border-slate-200 p-3 rounded-lg leading-relaxed">
                  {document.description || 'No scope description provided.'}
                </p>
              </div>

              {/* Cross Links */}
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Cross-Module Integrations (Bridges)</h4>
                {crossLinks.length === 0 ? (
                  <div className="text-xs text-slate-400 italic">No linked regulation or cross-module record found.</div>
                ) : (
                  <div className="space-y-2">
                    {crossLinks.map((cl) => (
                      <div key={cl.id} className="p-3 bg-slate-50 border border-slate-200 rounded-lg flex justify-between items-center text-xs">
                        <div>
                          <span className="font-semibold text-indigo-700 uppercase mr-2">[{cl.target_module}]</span>
                          <span className="font-mono font-medium text-slate-900">{cl.target_identifier}</span>
                          <span className="ml-2 text-slate-500">({cl.relationship_type})</span>
                        </div>
                        {cl.notes && <div className="text-slate-600">{cl.notes}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'workflow' && (
            <div className="space-y-6">
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                <h4 className="text-sm font-bold text-slate-800 mb-2">Controlled Lifecycle & Segregation of Duties (SoD)</h4>
                <p className="text-xs text-slate-600 mb-4">
                  In compliance with EASA ORO.GEN.200, the author cannot be the final approver (Accountable Manager) of their own document.
                </p>

                <div className="flex flex-wrap gap-3">
                  {document.status === 'DRAFT' && (
                    <button
                      onClick={handleRequestReview}
                      className="px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm"
                    >
                      Submit for Technical Review (Quality Reviewer)
                    </button>
                  )}

                  {['DRAFT', 'PENDING_REVIEW', 'CHANGES_REQUIRED'].includes(document.status) && (
                    <button
                      onClick={handleSubmitForApproval}
                      className="px-4 py-2 text-xs font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-lg shadow-sm"
                    >
                      Submit for Approval (Accountable Manager)
                    </button>
                  )}

                  {['APPROVED', 'PENDING_APPROVAL'].includes(document.status) && (
                    <button
                      onClick={handlePublish}
                      className="px-4 py-2 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow-sm"
                    >
                      Publish Document (Effective)
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'versions' && (
            <div className="space-y-3">
              {versions.map((ver) => (
                <div key={ver.id} className="p-4 bg-white border border-slate-200 rounded-xl flex justify-between items-center shadow-sm">
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-mono font-bold text-indigo-700 text-base">v{ver.version_number}</span>
                      <span className="text-xs px-2 py-0.5 rounded bg-slate-100 font-semibold text-slate-700">{ver.status}</span>
                      {ver.is_current === 1 && (
                        <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">Current Version</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-600 mt-1">{ver.change_summary || 'No revision summary provided.'}</div>
                    <div className="text-xs text-slate-400 mt-0.5">Created by: {ver.created_by_name} • {ver.created_at.slice(0, 16)}</div>
                  </div>
                  {ver.file_name && (
                    <div className="text-right">
                      <div className="text-xs font-semibold text-slate-700">{ver.file_name}</div>
                      <div className="text-xs text-indigo-600 font-medium cursor-pointer hover:underline">View PDF</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {activeTab === 'acks' && (
            <div className="space-y-3">
              <div className="text-xs text-slate-600 font-medium mb-2">
                Personnel required to acknowledge and review the active version of this document:
              </div>
              {acks.length === 0 ? (
                <div className="text-xs text-slate-400 italic">No acknowledgement records found. Automatically generated upon document publication.</div>
              ) : (
                <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
                  {acks.map((ack) => (
                    <div key={ack.id} className="p-3 bg-white flex justify-between items-center text-xs">
                      <div>
                        <div className="font-semibold text-slate-900">{ack.user_name}</div>
                        <div className="text-slate-500">Assigned: {ack.assigned_at?.slice(0, 10)} | Due Date: {ack.due_date?.slice(0, 10)}</div>
                      </div>
                      <div>
                        {ack.status === 'ACKNOWLEDGED' ? (
                          <span className="px-2.5 py-1 rounded bg-emerald-100 text-emerald-800 font-semibold">
                            Acknowledged ({ack.acknowledged_at?.slice(0, 10)})
                          </span>
                        ) : ack.status === 'OVERDUE' ? (
                          <span className="px-2.5 py-1 rounded bg-rose-100 text-rose-800 font-semibold">
                            OVERDUE
                          </span>
                        ) : (
                          <span className="px-2.5 py-1 rounded bg-amber-100 text-amber-800 font-semibold">
                            Pending Acknowledgement
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-3">
              <div className="relative border-l-2 border-slate-200 ml-4 space-y-4">
                {history.map((h) => (
                  <div key={h.id} className="relative pl-6 text-xs">
                    <div className="absolute -left-1.5 top-1.5 w-3 h-3 rounded-full bg-indigo-600 border-2 border-white" />
                    <div className="font-semibold text-slate-800">
                      {h.from_status} &rarr; <span className="text-indigo-600">{h.to_status}</span>
                    </div>
                    <div className="text-slate-600 mt-0.5">{h.reason}</div>
                    <div className="text-slate-400 mt-0.5">{h.actor_name} • {h.created_at.slice(0, 19)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end bg-slate-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
